import React from "react";
import type { PositionResolutionView, RequestDetail } from "@/api";
import { Button, Dialog, StatusPill } from "@/ds";
import { Icon } from "@/lib";
import { useT } from "../../i18n";
import {
  reviewGuidanceNextActionKey,
  reviewGuidanceSummaryKey,
  type ReviewGuidanceModel,
} from "../review-guidance";
import { generateControlDisabled } from "../screen-workspace.logic";
import { reviewActionModel } from "./model";
import type { ReadinessLocalIssue } from "./send-readiness";
import styles from "./email-review.module.css";
import bar from "./review-actions.module.css";
import { formatByteSize } from "./attachment-strip";
import { positionResolutionPresentation } from "./position-resolution";
import { Menu, type MenuItem } from "./menu";

type AsyncAction = () => Promise<boolean>;

function ConfirmedAttachments({ draft }: { draft: RequestDetail["draft"] }) {
  const t = useT();
  const attachments = draft?.attachments ?? [];
  if (attachments.length === 0) return null;
  return (
    <div className={styles.confirmedAttachments}>
      <strong>{t("ws.confirmedAttachments")}</strong>
      <ul>
        {attachments.map((attachment) => (
          <li key={attachment.id}>{attachment.filename} ({formatByteSize(attachment.byte_size)})</li>
        ))}
      </ul>
    </div>
  );
}

/** The copy for a refused new processing: someone who can assign is told to assign the request. */
function processBlockedKey(reason: string, canAssign: boolean) {
  if (reason === "REQUEST_NOT_ASSIGNED") return canAssign ? "workflow.error.REQUEST_NOT_ASSIGNED" : "solGuide.permission.REQUEST_NOT_ASSIGNED";
  return reason === "process_unavailable" ? "solGuide.processUnavailable" : `solGuide.permission.${reason}`;
}

export function ReviewActions({
  draft,
  sendReadiness,
  sendChecklistVisible = false,
  factsContext = false,
  factEditing = false,
  readinessLocalIssue,
  workflowProcess,
  compactSummary,
  recoveryAction,
  guidance,
  automaticProcessing,
  onRecompute,
  recomputing,
  editing,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  dirty,
  editInvalid = false,
  aiOn,
  generating,
  analysisInFlight = false,
  onGenerate,
  proposalControlsChanged,
  decisionActionsBlocked,
  writerActionsBlocked,
  onReject,
  onApprove,
  onApproveAndSend,
  onSend,
  pendingSubmissionId,
  onContinueSubmission,
  sendRecoveryState,
  onGotoSettings,
  positionResolution,
  onReviewFacts,
  taskGate = null,
  taskGateReason = null,
  taskGateDecidable = false,
  onOpenTasks,
  notices,
}: {
  draft: RequestDetail["draft"];
  sendReadiness?: RequestDetail["send_readiness"];
  sendChecklistVisible?: boolean;
  factsContext?: boolean;
  factEditing?: boolean;
  readinessLocalIssue?: ReadinessLocalIssue | null;
  compactSummary?: string;
  /** `hidden`: the step is already on screen, so no button names it, and nothing else takes the primary action. */
  recoveryAction?: { label: string; onClick: () => void; hidden?: boolean };
  /** `blocked` names why the server refuses a new processing (#2235); `onAssign` opens the assignment. */
  workflowProcess?: { enabled: boolean; busy: boolean; recovering: boolean; recalculationRequired?: boolean; previousCost?: number | null; blocked?: string | null; onAssign?: () => void };
  guidance: ReviewGuidanceModel;
  automaticProcessing: boolean;
  onRecompute: () => void;
  recomputing: boolean;
  editing: boolean;
  onStartEdit: () => void;
  onSaveEdit: AsyncAction;
  onCancelEdit: () => void;
  dirty: boolean;
  editInvalid?: boolean;
  aiOn: boolean;
  generating: boolean;
  /** #678: a queued/running analysis owns the request — a manual run would race it. */
  analysisInFlight?: boolean;
  onGenerate: () => void;
  proposalControlsChanged: boolean;
  decisionActionsBlocked: boolean;
  writerActionsBlocked: boolean;
  onReject: (reason: string) => Promise<boolean>;
  onApprove: AsyncAction;
  onApproveAndSend: () => Promise<boolean>;
  onSend: AsyncAction;
  pendingSubmissionId?: string | null;
  onContinueSubmission?: AsyncAction;
  sendRecoveryState: "awaiting_authority" | "uncertain" | null;
  onGotoSettings?: () => void;
  positionResolution?: PositionResolutionView | null;
  onReviewFacts?: () => void;
  /**
   * #2130: the server's reason a single-agent draft is not approvable yet. `resolve_tasks` is a
   * task the operator settles in request details; `correct_request` needs a corrected fact
   * or source and a new processing. The general "manual review" wording names neither.
   * #2134: `no_reply` is a result that carries no reply at all; `taskGateReason` is the reason
   * that result gives for it, and `taskGateDecidable` says that an open task still takes a
   * decision, which the next processing reads.
   */
  taskGate?: "resolve_tasks" | "correct_request" | "no_reply" | null;
  taskGateReason?: string | null;
  taskGateDecidable?: boolean;
  onOpenTasks?: () => void;
  notices?: React.ReactNode;
}) {
  const t = useT();
  const costHintId = React.useId();
  const moreRef = React.useRef<HTMLButtonElement | null>(null);
  const [rejectReason, setRejectReason] = React.useState("");
  /**
   * #1468: rejecting owns the action row the way editing does, instead of opening a sheet over it.
   * While it is on, the row carries the reason and its two decisions and nothing else — there is
   * no second place to look and no other action to take by mistake.
   */
  const [rejecting, setRejecting] = React.useState(false);
  const rejectInvokerRef = React.useRef<HTMLElement | null>(null);
  const reasonRef = React.useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = React.useState<"save" | "reject" | "approve" | "approveSend" | "send" | null>(null);
  const busyRef = React.useRef(false);
  // The newer-message analysis transition owns decision recomputation. The canonical
  // stale-decision guidance still says RECOMPUTE_DECISION, but presenting that manual
  // action here would race or bypass the worker and contradict the processing notice.
  // Keep authoritative guidance for enforcement and replace only its operator-facing
  // action while this narrowly validated transition is active.
  const primaryAction = automaticProcessing ? "WAIT" : guidance.next_action.code;
  // Only while the open task is what the guidance itself reports first; any stronger blocker keeps its own words.
  const gate = primaryAction === "REVIEW_EVIDENCE" && guidance.summary_code === "HUMAN_REVIEW_REQUIRED" ? taskGate : null;
  // For Sol, "decision safety blocked" only means that no current result exists; the guided summary
  // names why, for example a failed provider call (#2232).
  const summaryText = gate ? t(`workflow.gate.${gate}.summary`)
    : compactSummary && guidance.summary_code === "DECISION_SAFETY_BLOCKED" ? compactSummary : t(reviewGuidanceSummaryKey(guidance));
  // A failed submission is an error, not a request for action, so it keeps the red error tone (#1801).
  const guidanceTone = guidance.summary_code === "SUBMISSION_FAILED" ? "danger" : guidance.primary_blocker?.severity
    ?? (guidance.state === "ready" || guidance.state === "complete" ? "success" : "info");
  const nextActionText = t(reviewGuidanceNextActionKey({ code: primaryAction }));
  const reviewState = draft?.review_state || "pending";
  const terminal = guidance.state === "complete"
    || guidance.summary_code === "SENT"
    || guidance.summary_code === "CLOSED_REQUEST";
  const stale = guidance.primary_blocker?.code === "STALE_DRAFT"
    || guidance.additional_blockers.some((item) => item.code === "STALE_DRAFT");
  const humanReview = guidance.primary_blocker?.code === "HUMAN_REVIEW_REQUIRED"
    || guidance.additional_blockers.some((item) => item.code === "HUMAN_REVIEW_REQUIRED");
  const sendingBlocked = guidance.primary_blocker?.code === "SENDING_DISABLED"
    || guidance.additional_blockers.some((item) => item.code === "SENDING_DISABLED");
  const reconciliationPending = primaryAction === "RECONCILE_DELIVERY" || sendRecoveryState !== null;
  const submissionPending = primaryAction === "CONTINUE_SUBMISSION";
  const resolution = editing || dirty
    ? null
    : positionResolutionPresentation(positionResolution);
  const actionText = sendRecoveryState === "uncertain"
    ? t("ws.guidance.action.RECONCILE_DELIVERY")
    : sendRecoveryState === "awaiting_authority"
      ? t("ws.review.awaitingAuthoritativeState")
      : proposalControlsChanged
        ? t("ws.regenerateDraftWithPositions")
        : gate
          ? t(`workflow.gate.${gate}.action`)
          : primaryAction === "REVIEW_EVIDENCE" && resolution
            ? t(resolution.actionKey)
            : nextActionText;
  const blockerText = sendRecoveryState
    ? t("ws.review.retryBlocked")
    : proposalControlsChanged
      ? t("ws.positionsChangedBody")
      : gate
        ? [t(`workflow.gate.${gate}.body`), gate === "no_reply" && taskGateDecidable ? t("workflow.gate.no_reply.decide") : "",
          gate === "no_reply" && taskGateReason ? t("workflow.gate.no_reply.reason", { reason: taskGateReason }) : ""].filter(Boolean).join(" ")
        : primaryAction === "REVIEW_EVIDENCE" && resolution
          ? t(resolution.bodyKey)
          : null;
  const actions = reviewActionModel({
    hasDraft: Boolean(draft),
    reviewState,
    guidanceState: guidance.state,
    nextAction: primaryAction,
    proposalControlsChanged,
    editing,
    decisionActionsBlocked,
    writerActionsBlocked,
    stale,
    humanReview,
    terminal,
    reconciliationPending: reconciliationPending || submissionPending,
  });
  actions.canApprove = actions.canApprove && Boolean(draft?.outbound_artifact);
  actions.canSend = actions.canSend && Boolean(draft?.outbound_artifact);
  const alwaysShowSend = sendReadiness !== undefined && Boolean(draft);
  // Terminal and recovery states have their own status/action. A retained reply's blockers
  // do not describe more work to send it again.
  const processing = automaticProcessing || analysisInFlight || generating || Boolean(workflowProcess?.busy);
  const showReadinessSend = alwaysShowSend && !terminal && !reconciliationPending && !submissionPending
    && !processing && !editing && !factEditing;
  const openCount = (sendReadiness?.blockers.length ?? 0) + (readinessLocalIssue ? 1 : 0);
  const readinessCanSend = Boolean(sendReadiness?.ready && draft?.outbound_artifact && !readinessLocalIssue
    && !editing && !dirty && !proposalControlsChanged && !reconciliationPending && !submissionPending && !terminal);
  const currentDraftKey = draft ? `${draft.id}:${draft.version}:${draft.outbound_artifact?.content_hash ?? ""}` : null;
  // Keep attachment details beside approval/send; the recipient stays in the draft header.
  const sendShaped = Boolean(draft) && !terminal && !reconciliationPending && !submissionPending
    && (actions.canApprove || actions.canSend);
  /** The row exists at all only outside these states; rejection can only own a row that is there. */
  const actionRowOpen = !terminal && !reconciliationPending && !submissionPending;
  const startReject = (invoker?: HTMLElement | null) => {
    rejectInvokerRef.current = invoker ?? null;
    setRejecting(true);
  };

  const leaveReject = React.useCallback((restoreFocus: boolean) => {
    setRejecting(false);
    setRejectReason("");
    if (!restoreFocus) return;
    const invoker = rejectInvokerRef.current;
    rejectInvokerRef.current = null;
    // Rejection replaces the toolbar, so its menu trigger remounts after this state update.
    window.requestAnimationFrame(() => {
      const target = moreRef.current ?? invoker;
      if (target?.isConnected) target.focus({ preventScroll: true });
    });
  }, []);

  const cancelReject = React.useCallback(() => {
    if (busy) return;
    leaveReject(true);
  }, [busy, leaveReject]);

  // The row entering rejection puts the caret where the operator has to type.
  React.useLayoutEffect(() => {
    if (rejecting) reasonRef.current?.focus({ preventScroll: true });
  }, [rejecting]);

  const hasPrimaryControl = primaryAction === "RECOMPUTE_DECISION"
    || primaryAction === "GENERATE_DRAFT"
    || primaryAction === "REGENERATE_DRAFT"
    || (primaryAction === "REVIEW_DRAFT" && Boolean(draft))
    || (primaryAction === "REVIEW_EVIDENCE" && resolution?.target === "facts" && Boolean(onReviewFacts))
    || (primaryAction === "ENABLE_SENDING" && Boolean(onGotoSettings))
    || (primaryAction === "SEND_DRAFT" && actions.canSend);
  const showActionCopy = terminal
    || reconciliationPending
    || proposalControlsChanged
    || !hasPrimaryControl
    || primaryAction === "REVIEW_EVIDENCE";
  const showActionState = showActionCopy || dirty;

  /**
   * #589: publish how much of the viewport bottom this sticky bar occupies, so the toast stack
   * and focused review controls can clear it. Below 580px it can wrap from 72px to ~192px, so
   * the distance from its top edge to the viewport bottom is safer than its height alone.
   * The property lives on the document
   * element because the stack renders outside this subtree, and is removed on unmount so
   * every other screen keeps the plain corner offset.
   */
  const barRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    const node = barRef.current;
    // A DOM-less renderer (component tests) has neither the observer nor a layout to measure.
    if (!node || typeof ResizeObserver === "undefined") return;
    const root = document.documentElement;
    let frame = 0;
    let published = "";
    const publish = () => {
      frame = 0;
      const visible = node.getClientRects().length > 0 && getComputedStyle(node).visibility !== "hidden";
      const rect = node.getBoundingClientRect();
      const top = visible && rect.bottom > 0 ? Math.max(0, rect.top) : window.innerHeight;
      const next = `${Math.max(0, Math.round(window.innerHeight - top))}px`;
      // Writing a custom property invalidates document-wide style; only write on change.
      if (next === published) return;
      published = next;
      root.style.setProperty("--review-actions-clear", next);
    };
    // One layout read per frame, however many triggers fired into it.
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(publish);
    };
    publish();
    const observer = new ResizeObserver(schedule);
    observer.observe(node);
    // The bar also moves when content ABOVE it reflows (a shell banner mounting, say): its own
    // box is unchanged, no scroll happens, and such a reflow can even remove the page's
    // scrollability — so watching the bar alone would leave a stale clearance with no recovery.
    const shell = document.querySelector(".topshell__main");
    if (shell) observer.observe(shell);
    // Capture: the review grid scrolls in its own container on narrow widths, the document
    // scrolls on wide ones — one capturing listener covers both.
    window.addEventListener("scroll", schedule, { capture: true, passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("scroll", schedule, { capture: true });
      window.removeEventListener("resize", schedule);
      root.style.removeProperty("--review-actions-clear");
    };
  }, []);

  const cancelEditAndRestoreFocus = React.useCallback(() => {
    onCancelEdit();
    window.requestAnimationFrame(() => {
      document.querySelector<HTMLButtonElement>("[data-review-edit]")?.focus();
    });
  }, [onCancelEdit]);

  React.useEffect(() => {
    if (!editing) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      cancelEditAndRestoreFocus();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [cancelEditAndRestoreFocus, editing]);

  React.useEffect(() => {
    if (!rejecting) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      cancelReject();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [cancelReject, rejecting]);

  // Authority can be withdrawn, or the row can be taken over by a pending submission, while a
  // reason is being typed for a rejection nobody can make any more.
  React.useEffect(() => {
    if (rejecting && (!actions.canReject || !actionRowOpen)) leaveReject(false);
  }, [actionRowOpen, actions.canReject, leaveReject, rejecting]);

  const run = async (kind: NonNullable<typeof busy>, action: AsyncAction) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(kind);
    try {
      return await action();
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  };

  const submitReject = async () => {
    const reason = rejectReason.trim();
    if (!reason || !actions.canReject || !rejecting) return;
    if (await run("reject", () => onReject(reason))) leaveReject(false);
  };

  // Confirm only the artifact the operator opened, even if live invalidation replaces the
  // draft or withdraws authority while the dialog is visible.
  const [confirmation, setConfirmation] = React.useState<{
    kind: "approve" | "approveSend" | "send" | "continue"; key: string; draft: RequestDetail["draft"];
  } | null>(null);
  const confirmInvoker = React.useRef<HTMLElement | null>(null);
  const cancelRef = React.useRef<HTMLButtonElement | null>(null);
  const openConfirmation = (kind: NonNullable<typeof confirmation>["kind"], invoker?: HTMLElement | null) => {
    const key = kind === "continue" ? pendingSubmissionId && `${pendingSubmissionId}:${currentDraftKey}` : currentDraftKey;
    if (!key || busyRef.current) return;
    confirmInvoker.current = invoker ?? null;
    setConfirmation({ kind, key, draft });
  };
  const confirmationCurrent = Boolean(confirmation && confirmation.key === (confirmation.kind === "continue" ? pendingSubmissionId && `${pendingSubmissionId}:${currentDraftKey}` : currentDraftKey));
  const confirmationAllowed = confirmationCurrent && !busy && (confirmation?.kind === "continue"
    ? submissionPending && !reconciliationPending && !!onContinueSubmission
    : alwaysShowSend ? readinessCanSend
    : confirmation?.kind === "send" ? actions.canSend : actions.canApprove);
  const confirmAction = async () => {
    if (!confirmation || !confirmationAllowed) return;
    const { kind } = confirmation;
    // Close before dispatch, so the same confirmation cannot be reused after a failed command.
    setConfirmation(null);
    await run(kind === "continue" ? "send" : kind,
      kind === "continue" ? onContinueSubmission! : kind === "approve" ? onApprove : kind === "approveSend" ? onApproveAndSend : onSend);
  };

  // Assignment is the next action when every writer control is unavailable. Delivery recovery
  // and local editing retain their own controls, even if a new processing also needs assignment.
  const assignmentOnly = workflowProcess?.blocked === "REQUEST_NOT_ASSIGNED" && !workflowProcess.enabled
    && !editing && !dirty && !terminal && !reconciliationPending && !submissionPending
    && !actions.canEdit && !actions.canReject && !actions.canApprove && !actions.canSend;
  // A local correction needs save/cancel, not a new recovery action in an otherwise ready footer.
  const factEditorOnlyBlocker = factEditing && readinessLocalIssue === "local_changes"
    && sendReadiness?.ready === true && sendReadiness.blockers.length === 0
    && Boolean(draft?.outbound_artifact) && !dirty && !proposalControlsChanged;
  const workflowBlocked = Boolean(compactSummary) && !(alwaysShowSend ? readinessCanSend : sendShaped) && !terminal && !reconciliationPending
    && !submissionPending && !editing && !assignmentOnly && !factEditorOnlyBlocker;
  const showSummary = !compactSummary && !assignmentOnly;
  const showState = !assignmentOnly && (compactSummary ? dirty : showActionState);
  const processDisabled = generateControlDisabled({
    aiOn, generating: generating || Boolean(workflowProcess?.busy), analysisInFlight,
    decisionActionsBlocked: workflowProcess ? !workflowProcess.enabled : decisionActionsBlocked,
    terminal, dirty, reconciliationPending, busy: Boolean(busy),
  });
  const processReason = workflowProcess && processDisabled ? busy ? "solGuide.actionBusy"
    : generating || analysisInFlight || workflowProcess.busy ? "solGuide.processingWait"
    : dirty ? "solGuide.localChanges"
    : !aiOn ? "ws.aiDisabledBody"
    : workflowProcess.blocked ? processBlockedKey(workflowProcess.blocked, Boolean(workflowProcess.onAssign))
    : "solGuide.processUnavailable" : null;
  const sendProminent = alwaysShowSend ? readinessCanSend : sendShaped;
  const recoveryProminent = workflowBlocked && recoveryAction && !sendProminent && !workflowProcess?.recalculationRequired;
  const localEditor = editing || factEditing;
  const showRecovery = workflowBlocked && recoveryAction && !recoveryAction.hidden && !localEditor;
  const processRequired = Boolean(workflowProcess?.recovering || workflowProcess?.recalculationRequired
    || !draft || stale || reviewState === "rejected" || proposalControlsChanged
    || primaryAction === "GENERATE_DRAFT" || primaryAction === "REGENERATE_DRAFT");
  const showProcess = actionRowOpen && !processing && !localEditor && !rejecting && !assignmentOnly
    && Boolean(workflowProcess || primaryAction === "GENERATE_DRAFT" || primaryAction === "REGENERATE_DRAFT" || proposalControlsChanged)
    && (workflowProcess || primaryAction !== "RECOMPUTE_DECISION")
    && (!processDisabled || processRequired);
  const processPrimary = !sendProminent && !recoveryProminent && (processRequired || !workflowBlocked)
    && !(gate === "resolve_tasks" && !compactSummary)
    && !(primaryAction === "REVIEW_EVIDENCE" && resolution?.target === "facts" && onReviewFacts);
  const processLabel = workflowProcess
    ? t(workflowProcess.recovering ? "workspace.recoverSubmission" : "workflow.recalculate") : actionText;
  const processInMenu = showProcess && !processRequired;
  const generateFromMenu = () => {
    // The workspace captures document.activeElement for its paid-processing dialog's return focus.
    // The menu item unmounts on selection; the toolbar trigger remains connected.
    moreRef.current?.focus({ preventScroll: true });
    onGenerate();
  };
  const moreItems: MenuItem[] = [
    ...(processInMenu ? [{ icon: "refresh", label: workflowProcess && !workflowProcess.recovering
      ? t("reviewActions.recalculatePaid") : processLabel, onClick: generateFromMenu, disabled: processDisabled }] : []),
    ...(actions.canReject ? [{ icon: "xCircle", label: t("ws.reject"), danger: true,
      onClick: () => startReject(moreRef.current), disabled: Boolean(busy) }] : []),
  ];
  const showBlockedCount = showReadinessSend && !readinessCanSend && !rejecting && openCount > 0;
  const disabledActionHints = actionRowOpen && !processing ? [...new Set([
    !localEditor && processReason && t(processReason, undefined, t("solGuide.permission.unavailable")),
    editing && (!dirty || editInvalid) && t(editInvalid ? "solGuide.saveInvalid" : "solGuide.noEdits"),
    busy && t("solGuide.actionBusy"),
  ].filter((value): value is string => Boolean(value)))] : [];
  return (
    <>
      <div className={`${styles.actionContext} ${bar.context}`} data-testid="review-action-context">
        {notices && (
          <div className={styles.actionNotices} data-testid="review-action-notices">
            {notices}
          </div>
        )}
        {showProcess && !processInMenu && workflowProcess && !processDisabled && <p id={costHintId} className="muted">{t(workflowProcess.recovering ? "workspace.recoverSubmissionHint" : "solGuide.processCost")}</p>}
        {showProcess && workflowProcess?.recalculationRequired && typeof workflowProcess.previousCost === "number" && <p className="muted">
          {t("workflow.previousRunCost", { cost: (workflowProcess.previousCost / 1000000).toFixed(4) })}</p>}
        {factsContext && factEditing && <p role="status" className="subtle">{t("solFacts.finishEditing")}</p>}
        {disabledActionHints.length > 0 && <div className={styles.processBlocked} data-testid="process-blocked">
          <Icon name="lock" size={16} /><div>{disabledActionHints.map(hint => <p key={hint}>{hint}</p>)}</div>
        </div>}
        {(showSummary || showState) && <div className={styles.actionLead}>
          {showSummary && <div
            className={styles.statusRow}
            data-testid="review-status"
            data-tone={guidanceTone}
          >
            <div className={styles.guidanceSummary}>
              <span>{summaryText}</span>
            </div>
            {!compactSummary && guidance.additional_blocker_count > 0 && (
              <details className={styles.guidanceMore}>
                <summary>{t("ws.additionalBlockers", { n: guidance.additional_blocker_count })}</summary>
                <ul>
                  {guidance.additional_blockers.map((blocker) => (
                    <li key={blocker.code}>{t(`ws.guidance.summary.${blocker.code}`)}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>}
          {showState && <div className={styles.actionState}>
            {!compactSummary && showActionCopy && <>
              <strong>{actionText}</strong>
              {blockerText && <span>{blockerText}</span>}
            </>}
            {dirty && <StatusPill size="sm" tone="info" label={t("ws.unsavedChange")} />}
          </div>}
        </div>}

        {sendShaped && Boolean(draft?.attachments?.length) && <div className={styles.confirmedDetails} data-testid="review-send-details">
          <ConfirmedAttachments draft={draft} />
        </div>}
        {!compactSummary && sendShaped && sendingBlocked && <p className="muted">{t("ws.guidance.summary.SENDING_DISABLED")}</p>}
      </div>
      <div ref={barRef} className={`${styles.actions} ${bar.bar}`} data-testid="review-actions" data-guided={Boolean(compactSummary)}>
        {processing && actionRowOpen && !localEditor && <span className={bar.status} role="status">
          <Icon name="clock" size={16} />{t("reviewActions.processing")}
        </span>}
        {showBlockedCount && <span className={bar.status} role="status"
          title={t(sendChecklistVisible ? "solGuide.sendChecklistHere" : "solGuide.sendChecklist", {
            tab: t("ws.review.tab.draft"), checklist: t("sendReady.title"),
          })}>
          <Icon name="list" size={16} />{t(openCount === 1 ? "sendReady.openOne" : "sendReady.open", { n: openCount })}
        </span>}
        {actionRowOpen && !localEditor && !processing && workflowProcess?.onAssign && <Button size="md" variant={assignmentOnly ? "primary" : "secondary"} onClick={workflowProcess.onAssign}>
          {t("solGuide.assignment")}
        </Button>}
        {submissionPending && !reconciliationPending && <Button size="md" variant="primary" iconLeft={<Icon name="send" size={16} />}
          disabled={!pendingSubmissionId || !onContinueSubmission || Boolean(busy)}
          loading={busy === "send"} onClick={event => openConfirmation("continue", event?.currentTarget)}>
          {t("ws.guidance.action.CONTINUE_SUBMISSION")}
        </Button>}
        {actionRowOpen && rejecting && <div className={bar.rejectRow} data-testid="review-reject-row">
          <label className={bar.reason}>
            <span>{t("ws.review.rejectReason")}</span>
            <input ref={reasonRef} data-review-reject-reason className="ribbon-field" type="text"
              value={rejectReason} disabled={!actions.canReject || Boolean(busy)}
              aria-label={t("ws.review.rejectReason")} placeholder={t("ws.review.rejectPlaceholder")}
              onChange={(event) => setRejectReason(event.currentTarget.value)} />
          </label>
          <Button variant="ghost" size="md" disabled={Boolean(busy)} onClick={cancelReject}>{t("common.cancel")}</Button>
          <Button variant="destructive" size="md" loading={busy === "reject"}
            disabled={!actions.canReject || !rejectReason.trim() || Boolean(busy)} onClick={() => { void submitReject(); }}>
            {t("ws.reject")}
          </Button>
        </div>}
        {actionRowOpen && !rejecting && editing && <div className={bar.controls}>
          <Button variant="ghost" size="md" disabled={Boolean(busy)} onClick={cancelEditAndRestoreFocus}>{t("common.cancel")}</Button>
          <Button variant="primary" size="md" loading={busy === "save"} disabled={!dirty || editInvalid || Boolean(busy)}
            iconLeft={<Icon name="check" size={16} />} onClick={() => { void run("save", onSaveEdit); }}>
            {t("ws.review.save")}
          </Button>
        </div>}
        {actionRowOpen && !rejecting && !localEditor && !assignmentOnly && <>
          {!processing && !workflowProcess?.recovering && (actions.canEdit || moreItems.length > 0) && <div className={bar.secondary}>
            {actions.canEdit && <Button data-review-edit variant="secondary" size="md" disabled={Boolean(busy)}
              iconLeft={<Icon name="pencil" size={16} />} onClick={onStartEdit}>{t("ws.edit")}</Button>}
            {moreItems.length > 0 && <Menu placement="top" align="right" boundaryRef={barRef} triggerRef={moreRef} items={moreItems}
              trigger={<button ref={moreRef} type="button" data-review-more className="neo-btn neo-btn--ghost" disabled={Boolean(busy)}>
                {t("reviewActions.more")}<Icon name="chevronDown" size={16} />
              </button>} />}
          </div>}
          <div className={bar.controls}>
            {showProcess && !processInMenu && <Button variant={processPrimary ? "primary" : "ghost"} size="md" disabled={processDisabled}
              iconLeft={<Icon name="refresh" size={16} />} onClick={onGenerate}
              aria-describedby={workflowProcess && !workflowProcess.recovering && !processDisabled ? costHintId : undefined}>
              {processLabel}
            </Button>}
            {showRecovery && <Button variant={processing || !recoveryProminent || processPrimary ? "secondary" : "primary"} size="md"
              iconLeft={<Icon name="fileText" size={16} />} onClick={recoveryAction.onClick}>{recoveryAction.label}</Button>}
            {!processing && !workflowProcess && primaryAction === "RECOMPUTE_DECISION" && <Button variant="primary" size="md"
              loading={recomputing} disabled={recomputing || Boolean(busy)} iconLeft={<Icon name="refresh" size={16} />} onClick={onRecompute}>
              {nextActionText}
            </Button>}
            {!processing && !compactSummary && (gate === "resolve_tasks" || gate === "no_reply" && taskGateDecidable) && onOpenTasks && <Button
              variant={gate === "no_reply" ? "secondary" : "primary"} size="md" iconLeft={<Icon name="list" size={16} />} onClick={onOpenTasks}>
              {t("workflow.gate.openTasks")}
            </Button>}
            {!processing && !gate && primaryAction === "REVIEW_EVIDENCE" && resolution?.target === "facts" && onReviewFacts && <Button
              variant={compactSummary ? "secondary" : "primary"} size="md" iconLeft={<Icon name="list" size={16} />} onClick={onReviewFacts}>
              {t(resolution.actionKey)}
            </Button>}
            {!processing && !alwaysShowSend && actions.canApprove && <Button size="md" variant="primary"
              iconLeft={<Icon name={sendingBlocked ? "thumbsUp" : "send"} size={16} />} disabled={Boolean(busy)}
              loading={busy === "approve" || busy === "approveSend"}
              onClick={event => openConfirmation(sendingBlocked ? "approve" : "approveSend", event?.currentTarget)}>
              {t(sendingBlocked ? "ws.approve" : "ws.approveAndSend")}
            </Button>}
            {!processing && !alwaysShowSend && actions.canSend && <Button size="md" variant="primary" iconLeft={<Icon name="send" size={16} />}
              disabled={Boolean(busy)} loading={busy === "send"} onClick={event => openConfirmation("send", event?.currentTarget)}>
              {t("ws.sendLabel")}
            </Button>}
            {!processing && primaryAction === "ENABLE_SENDING" && onGotoSettings && <Button variant={showProcess && processPrimary ? "secondary" : "primary"} size="md" onClick={onGotoSettings}>
              {t("ws.enableInSettings")}
            </Button>}
            {showReadinessSend && readinessCanSend && <Button size="md" variant="primary" iconLeft={<Icon name="send" size={16} />}
              disabled={Boolean(busy)} loading={busy === "approveSend"} onClick={event => openConfirmation("approveSend", event?.currentTarget)}>
              {t("ws.approveAndSend")}
            </Button>}
          </div>
        </>}
      </div>
      <Dialog open={confirmation !== null} closeButton={false} title={t(confirmation?.kind === "continue" ? "sendReady.confirmContinue" : confirmation?.kind === "approve" ? "sendReady.confirmApprove" : "sendReady.confirmSend")}
        onClose={busy ? undefined : () => setConfirmation(null)} initialFocusRef={cancelRef} finalFocusRef={confirmInvoker}
        footer={<>
          <Button ref={cancelRef} variant="ghost" disabled={Boolean(busy)} onClick={() => setConfirmation(null)}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={!confirmationAllowed} onClick={() => { void confirmAction(); }}>
            {t(confirmation?.kind === "continue" ? "ws.guidance.action.CONTINUE_SUBMISSION" : confirmation?.kind === "approve" ? "ws.approve" : "sendReady.sendNow")}
          </Button>
        </>}>
        <div className="stack-sm">
          <p>{t(confirmation?.kind === "continue" ? "sendReady.continueHint" : confirmation?.kind === "approve" ? "sendReady.approveHint" : "sendReady.sendHint")}</p>
          {/* The send records this statement for each point it lists (#2516); the server refuses another list. */}
          {confirmation?.kind === "approveSend" && !!sendReadiness?.settled_at_send?.length && <div>
            <p>{t("sendReady.atSend.confirm")}</p>
            <ul>{sendReadiness.settled_at_send.map(item => <li key={item.task_id}>
              {t(`workflow.task.${item.task_kind}`)}{item.field ? `: ${t(`field.${item.field}`)}` : ""}
            </li>)}</ul>
          </div>}
          {confirmation?.draft?.outbound_artifact ? <><dl>
            <dt>{t("sendReady.recipient")}</dt><dd>{confirmation?.draft?.outbound_artifact?.to ?? "—"}</dd>
            <dt>{t("sendReady.subject")}</dt><dd>{confirmation?.draft?.outbound_artifact?.subject ?? "—"}</dd>
          </dl>
          {confirmation.draft.attachments?.length ? <ConfirmedAttachments draft={confirmation.draft} /> : <p>{t("sendReady.noAttachments")}</p>}
          </> : <p>{t("sendReady.continuePreviewUnavailable")}</p>}
          {!confirmationCurrent && <p role="alert">{t("sendReady.confirmChanged")}</p>}
          {confirmationCurrent && !confirmationAllowed && <p role="alert">{t("sendReady.confirmBlocked")}</p>}
        </div>
      </Dialog>
    </>
  );
}
